import { FC, useEffect, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { useDispatch } from 'react-redux';

// PACKAGES
import { Modal, Spinner } from 'react-bootstrap';

// COMPONENTS
import SvgMask from '../../../components/_Helpers/SvgMask';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import {
	AssessmentMoveListAssessmentPeriod,
	AssessmentMoveListAssessmentType,
	AssessmentValidateMoveCommand,
	AssessmentMoveCommand
} from '../../../api/models';

interface MoveAssessmentMultipleProps {
	show: boolean;
	onHide: () => void;
	selectedMovingStudents: string[];
	onMoveSuccess: () => void;
}

const MoveAssessmentMultiple: FC<MoveAssessmentMultipleProps> = ({
	show,
	onHide,
	selectedMovingStudents,
	onMoveSuccess
}) => {
	const dispatch = useDispatch();

	const [isLoadingMoveAssessments, setIsLoadingMoveAssessments] = useState(false);
	const [isMovingAssessment, setIsMovingAssessment] = useState(false);
	const [isValidatingAssessmentMove, setIsValidatingAssessmentMove] = useState(false);

	// View State
	const [moveModalView, setMoveModalView] = useState<'initial' | 'review'>('initial');

	// Error State
	const [showNoRecordsError, setShowNoRecordsError] = useState(false);

	// Validation State
	const [isValidateWillOverwrite, setIsValidateWillOverwrite] = useState(false);

	// Assessment Types and Periods
	const [assessmentMoveTypes, setAssessmentMoveTypes] = useState<AssessmentMoveListAssessmentType[]>([]);
	const [assessmentMovePeriods, setAssessmentMovePeriods] = useState<AssessmentMoveListAssessmentPeriod[]>([]);

	// All Selection State
	const [selectedAssessmentMoveType, setSelectedAssessmentMoveType] = useState<AssessmentMoveListAssessmentType | null>(
		null
	);
	const [selectedAssessmentMovePeriodFrom, setSelectedAssessmentMovePeriodFrom] =
		useState<AssessmentMoveListAssessmentPeriod | null>(null);
	const [selectedAssessmentMovePeriodTo, setSelectedAssessmentMovePeriodTo] =
		useState<AssessmentMoveListAssessmentPeriod | null>(null);

	const getAssessmentMoveListData = async () => {
		setIsLoadingMoveAssessments(true);

		const response = await apibridge.getAssessmentMoveList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentMoveTypes(response.data.result.assessmentTypes || []);
				setAssessmentMovePeriods(response.data.result.assessmentPeriods || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Move List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoadingMoveAssessments(false);
	};

	useEffect(() => {
		if (show) {
			getAssessmentMoveListData();
		}
	}, [show]); // eslint-disable-line react-hooks/exhaustive-deps

	// Validate before Move
	const validateAssessmentMove = async () => {
		if (
			!selectedAssessmentMoveType ||
			!selectedAssessmentMovePeriodFrom ||
			!selectedAssessmentMovePeriodTo ||
			selectedMovingStudents.length === 0
		) {
			console.error('Missing required parameters for validation');
			return;
		}

		setIsValidatingAssessmentMove(true);

		const validateData: AssessmentValidateMoveCommand = {
			studentIds: selectedMovingStudents,
			currentAssessmentPeriodId: selectedAssessmentMovePeriodFrom?.key,
			newAssessmentPeriodId: selectedAssessmentMovePeriodTo?.key,
			type: selectedAssessmentMoveType?.type,
			assessmentId: selectedAssessmentMoveType?.id
		};

		const response = await apibridge.postAssessmentValidateMove(validateData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setIsValidateWillOverwrite(response.data.result.willOverwrite || false);

				if (response.data.result.success === false) {
					setShowNoRecordsError(true);
				} else {
					setShowNoRecordsError(false);
					setMoveModalView('review');
				}
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Validate Move error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsValidatingAssessmentMove(false);
	};

	// Moving Assessment
	const moveAssessment = async () => {
		if (
			!selectedAssessmentMoveType ||
			!selectedAssessmentMovePeriodFrom ||
			!selectedAssessmentMovePeriodTo ||
			selectedMovingStudents.length === 0
		) {
			console.error('Missing required parameters for moving assessment');
			return;
		}

		setIsMovingAssessment(true);

		const moveData: AssessmentMoveCommand = {
			studentIds: selectedMovingStudents,
			currentAssessmentPeriodId: selectedAssessmentMovePeriodFrom?.key,
			newAssessmentPeriodId: selectedAssessmentMovePeriodTo?.key,
			type: selectedAssessmentMoveType?.type,
			assessmentId: selectedAssessmentMoveType?.id
		};

		const response = await apibridge.postAssessmentMove(moveData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				// Save the selected period labels before resetting state
				const movedFromLabel = selectedAssessmentMovePeriodFrom?.label;
				const movedToLabel = selectedAssessmentMovePeriodTo?.label;

				// Close modal and show success message
				handleClose();

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Assessment moved successfully',
						description: `Assessment records have been successfully moved from ${movedFromLabel} to ${movedToLabel}.`
					})
				);

				// Call parent callback to refresh data and reset state
				onMoveSuccess();
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Move error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsMovingAssessment(false);
	};

	// Handle modal close - reset state
	const handleClose = () => {
		setMoveModalView('initial');
		setSelectedAssessmentMoveType(null);
		setSelectedAssessmentMovePeriodFrom(null);
		setSelectedAssessmentMovePeriodTo(null);
		setShowNoRecordsError(false);
		setIsValidateWillOverwrite(false);
		onHide();
	};

	return (
		<Modal show={show} onHide={handleClose} centered className="move-assessment-multiple-modal">
			<Modal.Body className="d-flex flex-column">
				<h2 className="mb-3 text-shades-800">
					<strong>Move assessment records</strong>
				</h2>

				{moveModalView === 'initial' && (
					<>
						<p className="text-dark-text body-normal mb-4">
							Select records you want to move from one assessment period to another for the{' '}
							{selectedMovingStudents.length} student{selectedMovingStudents.length === 1 ? '' : 's'} selected.
						</p>

						<div className="row mb-2">
							<div className="col-3">
								<div className="d-flex align-items-center justify-content-end h-100">
									<label htmlFor="assessmentMoveType" className="body-normal fw-bold text-dark-text">
										Assessment type
									</label>
								</div>
							</div>

							<div className="col-9">
								<select
									value={selectedAssessmentMoveType?.id || ''}
									onChange={(e) => {
										const selectedType = assessmentMoveTypes.find((type) => type.id === e.target.value);
										setSelectedAssessmentMoveType(selectedType || null);

										// Reset Error and All Dropdowns if Error is Present
										if (showNoRecordsError) {
											setShowNoRecordsError(false);

											if (selectedAssessmentMovePeriodFrom) {
												setSelectedAssessmentMovePeriodFrom(null);
											}

											if (selectedAssessmentMovePeriodTo) {
												setSelectedAssessmentMovePeriodTo(null);
											}
										}
									}}
									className="form-select"
									name="assessmentMoveType"
									id="assessmentMoveType"
									disabled={isLoadingMoveAssessments}
								>
									<option value="" disabled>
										Please select an assessment type
									</option>

									{assessmentMoveTypes.map((assessmentType, index) => {
										const { id = '', name = '' } = assessmentType;
										return (
											<option key={index} value={id}>
												{name}
											</option>
										);
									})}
								</select>
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-3">
								<div className="d-flex align-items-center justify-content-end h-100">
									<label htmlFor="assessmentMoveFrom" className="body-normal fw-bold text-dark-text">
										From
									</label>
								</div>
							</div>

							<div className="col-9">
								<select
									value={selectedAssessmentMovePeriodFrom?.key || ''}
									onChange={(e) => {
										const selectedPeriod = assessmentMovePeriods.find((period) => period.key === e.target.value);
										setSelectedAssessmentMovePeriodFrom(selectedPeriod || null);

										if (selectedAssessmentMovePeriodTo) {
											setSelectedAssessmentMovePeriodTo(null);
										}

										if (showNoRecordsError) {
											setShowNoRecordsError(false);
										}
									}}
									className={`form-select ${showNoRecordsError ? 'has-error' : ''}`}
									name="assessmentMoveFrom"
									id="assessmentMoveFrom"
									disabled={isLoadingMoveAssessments}
								>
									<option value="" disabled>
										Please select an assessment period
									</option>

									{assessmentMovePeriods.map((assessmentPeriod, index) => {
										const { key = '', label = '' } = assessmentPeriod;
										return (
											<option key={index} value={key}>
												{label}
											</option>
										);
									})}
								</select>
							</div>
						</div>

						<div className="row mb-2">
							<div className="col-3">
								<div className="d-flex align-items-center justify-content-end h-100">
									<label htmlFor="assessmentMoveTo" className="body-normal fw-bold text-dark-text">
										Move to
									</label>
								</div>
							</div>

							<div className="col-9">
								<select
									value={selectedAssessmentMovePeriodTo?.key || ''}
									onChange={(e) => {
										const selectedPeriod = assessmentMovePeriods.find((period) => period.key === e.target.value);
										setSelectedAssessmentMovePeriodTo(selectedPeriod || null);
									}}
									className="form-select"
									name="assessmentMoveTo"
									id="assessmentMoveTo"
									disabled={isLoadingMoveAssessments}
								>
									<option value="" disabled>
										Please select an assessment period
									</option>

									{assessmentMovePeriods
										.filter((period) => period.key !== selectedAssessmentMovePeriodFrom?.key)
										.map((assessmentPeriod, index) => {
											const { key = '', label = '' } = assessmentPeriod;
											return (
												<option key={index} value={key}>
													{label}
												</option>
											);
										})}
								</select>
							</div>
						</div>

						{showNoRecordsError && (
							<div className="same-period-error-box">
								<SvgMask path="/svg/alert-outline.svg" width={24} height={24} />

								<div>
									<p className="mb-2 fw-semibold text-error body-small text-start">
										Could not find records for all selected students in{' '}
										<strong>{selectedAssessmentMovePeriodFrom?.label}</strong> .
									</p>

									<p className="mb-0 fw-semibold text-error body-small text-start">
										All students must have assessment data for the selected assessment period to proceed.
									</p>
								</div>
							</div>
						)}

						<div className="button-group d-flex gap-3 mt-0">
							<button
								type="button"
								onClick={handleClose}
								className="btn btn-white btn-lg w-100"
								disabled={isLoadingMoveAssessments || isValidatingAssessmentMove}
							>
								Cancel
							</button>

							<button
								type="button"
								onClick={validateAssessmentMove}
								className="btn btn-lg w-100"
								disabled={
									isLoadingMoveAssessments ||
									showNoRecordsError ||
									!selectedAssessmentMoveType ||
									!selectedAssessmentMovePeriodFrom ||
									!selectedAssessmentMovePeriodTo ||
									isValidatingAssessmentMove
								}
							>
								{isValidatingAssessmentMove ? (
									<Spinner animation="border" role="status" size="sm">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								) : (
									<> Continue to review</>
								)}
							</button>
						</div>
					</>
				)}

				{moveModalView === 'review' && (
					<>
						<p className="text-dark-text body-normal mb-4">
							Please confirm the information below is correct before moving all records from one assessment period to
							another.
						</p>

						<div className="multiple-assessment-move-notification-box">
							<div className="top-box">
								<div className="d-flex align-items-center justify-content-between gap-2 px-3 py-3">
									<div className="d-flex align-items-center gap-2 justify-content-start">
										<SvgMask path="/svg/assessment.svg" width={22} height={22} className="text-dark-text" />

										<span className="body-normal text-dark-text fw-bold">{selectedAssessmentMoveType?.name}</span>
									</div>

									<p className="mb-0 text-dark-text body-normal">
										{selectedMovingStudents.length} student record{selectedMovingStudents.length === 1 ? '' : 's'}
									</p>
								</div>
							</div>

							<div className="divider-line"></div>

							<div className="bottom-box">
								<div className="d-flex flex-column justify-content-center align-items-center w-50 py-3 ps-3 icon-divider-container">
									<span className="body-tiny text-shades-500 fw-bold">From</span>
									<span className="body-normal text-shades-800 fw-bold">{selectedAssessmentMovePeriodFrom?.label}</span>

									<div className="divider-icon">
										<SvgMask path="/svg/chevron-right.svg" width={16} height={16} />
									</div>
								</div>

								<div className="d-flex flex-column justify-content-center align-items-center py-3 pe-3 w-50">
									<span className="body-tiny text-shades-500 fw-bold">To</span>

									<div className="d-flex justify-content-center align-items-center gap-1">
										<span className="body-normal text-shades-800 fw-bold">{selectedAssessmentMovePeriodTo?.label}</span>

										{isValidateWillOverwrite && (
											<SvgMask path="/svg/triangle-yellow.svg" width={16} height={16} className="text-amber" />
										)}
									</div>
								</div>
							</div>
						</div>

						{isValidateWillOverwrite && (
							<div className="move-assessment-error-box">
								<SvgMask path={'/svg/caution-outline.svg'} width={20} height={20} />

								<div>
									<p className="mb-2 text-start">
										There is existing assessment data in the assessment period you are moving the selected records to.
									</p>

									<p className="mb-0 text-start">
										If you proceed, the records in <strong>{selectedAssessmentMovePeriodFrom?.label}</strong> will
										overwrite the records in <strong>{selectedAssessmentMovePeriodTo?.label}</strong>.
									</p>
								</div>
							</div>
						)}

						<div className="button-group d-flex gap-3 mt-0">
							<button
								type="button"
								onClick={() => {
									setMoveModalView('initial');
									setIsValidateWillOverwrite(false);
								}}
								className="btn btn-white btn-lg w-100"
								disabled={isMovingAssessment}
							>
								Back
							</button>

							<button type="button" onClick={moveAssessment} className="btn btn-lg w-100" disabled={isMovingAssessment}>
								{isMovingAssessment ? (
									<Spinner animation="border" role="status" size="sm">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								) : (
									<> Move records</>
								)}
							</button>
						</div>
					</>
				)}
			</Modal.Body>
		</Modal>
	);
};

export default MoveAssessmentMultiple;
